<template>
  <!-- prettier-ignore -->
  <div
    :class="classes"
    :data-product-id="product.id">
    <div
      v-if="product.recommended"
      class="preview__recommendation"
      v-text="$t(content).recommendation"
    />

    <div
      class="preview-content"
      @click="$emit('open')">
      <div
        class="preview__title"
        v-text="$t(content).name"
      />
      <div>
        <template v-if="showPrice">
          <ovp-price
            class="preview__price"
            :price="productPricing.price"
            :suffix="$t('product.currency')"
          />
          <ovp-icon
            v-if="reductions.length > 0"
            v-tooltip="reduction.label"
            art="light"
            html-tooltip
            name="badge-percent"
          />
        </template>
        <template v-else>
          <span class="price preview__price">
            <span
              class="price__value price__value--unavailable"
              v-text="$t('product.unavailable')"
            />
          </span>
        </template>
      </div>
      <div
        class="preview__description"
        v-text="$t(content).description"
      />
    </div>
    <div class="preview__actions">
      <ovp-button
        class="preview__action"
        plain
        @click="$emit('open')">
        {{ $t('product.more') }}
      </ovp-button>

      <ovp-icon
        v-if="tooltip"
        v-tooltip="tooltip"
        class="preview__tooltip"
        art="light"
        name="circle-info"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import OvpPrice from '@/components/Product/Price'
import ProductMixin from '@/mixins/Product'

export default {
  name: 'Preview',

  mixins: [ProductMixin],

  components: {
    OvpPrice,
  },

  props: {
    categoryId: {
      type: String,
      default: null,
    },

    personId: {
      type: String,
      required: true,
    },

    product: {
      type: Object,
      required: true,
    },
  },

  computed: {
    ...mapGetters('basket', ['getProductPrice']),

    classes() {
      return {
        preview: true,
        'preview--disabled': !this.product.valid && !this.product.selected,
        'preview--recommended': this.product.recommended,
        'preview--selected': this.product.selected,
      }
    },

    content() {
      return `content.products.${this.product.id}`
    },

    isDisabled() {
      return this.disabled || (!this.product.valid && !this.selected)
    },

    productPricing() {
      return this.getProductPrice(this.personId, this.product.id) || {}
    },

    reduction() {
      const labels = []

      this.reductions.forEach(r => labels.push(this.$t(`info.${r.type}`)))

      return {
        price: 0,
        label: labels.join('; '),
      }
    },

    reductions() {
      // TODO check if reductions always there
      return this.productPricing?.reductions ?? []
    },

    tooltip() {
      if (this.isDisabled) {
        let content = this.product.messages
          ?.map(m => {
            return this.$t(`error.${m.key}`)
          })
          .join('; ')
          .trim()

        if (!content) {
          content = this.$t(this.getContent(this.product.id, 'unavailable'))
        }

        return {
          allowHTML: true,
          content,
        }
      } else {
        return false
      }
    },
  },
}
</script>

<style name="mobile" scoped>
.preview {
  color: var(--c-black);
  user-select: none;
  cursor: pointer;
  transition: color 0.3s ease-in-out;

  &--recommended .preview-content {
    min-height: 130px;
  }

  &-content {
    display: flex;
    flex-direction: column;
    padding: 15px;
    min-height: 170px;

    .icon {
      font-size: 20px;
    }
  }

  &__recommendation {
    background-color: var(--c-sanitas-green);
    color: var(--c-white);
    font-weight: bold;
    font-size: 15px;
    padding: 5px 15px;
    flex: 1 1 100%;
  }

  &__title {
    font-size: 15px;
    line-height: 20px;
    font-weight: bold;
  }

  &__price {
    font-size: 25px;
    line-height: 35px;
    padding-right: 5px;

    .price__value--unavailable {
      font-size: 20px;
    }
  }

  &__description {
    margin-top: 15px;
  }

  .preview__actions {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin: auto 15px auto 10px;
    padding: 10px 5px 5px 0;

    .preview__action {
      height: 25px;
      min-height: 25px;
      justify-content: flex-start;
      color: var(--c-original-green);
    }

    .preview__tooltip {
      margin: 5px 0 0 0;
    }
  }

  &--selected.preview--selected.preview--selected {
    background-color: var(--c-original-green-lighten-3);
    border-color: var(--c-original-green-lighten-3);
  }

  &--disabled.preview--disabled.preview--disabled {
    .preview__title,
    .preview__price,
    .preview__description {
      opacity: 0.5;
      border-color: var(--c-light-gray);
    }
  }

  &:hover {
    color: var(--c-original-green);
  }
}
</style>
